import React from 'react';

const cx = (...parts) => parts.filter(Boolean).join(' ');

const THEMES = {
  blue: {
    rail: 'bg-gradient-to-b from-primary-500 to-primary-700',
    glow: 'ring-primary-100/80',
  },
  purple: {
    rail: 'bg-gradient-to-b from-creative-500 to-accent-indigo',
    glow: 'ring-creative-100/80',
  },
  emerald: {
    rail: 'bg-gradient-to-b from-emerald-500 to-emerald-700',
    glow: 'ring-emerald-100/80',
  },
  orange: {
    rail: 'bg-gradient-to-b from-warning-500 to-accent-amber',
    glow: 'ring-warning-100/80',
  },
  pink: {
    rail: 'bg-gradient-to-b from-playful-500 to-accent-pink',
    glow: 'ring-playful-100/80',
  },
};

/**
 * ExpressiveCard
 * - variant="glass": premium glass container (double stroke)
 * - variant="ticket": glass container + left rail + pin holes
 *
 * Notes:
 * - Glass effect MUST have: border-white/60 AND ring-1 ring-white/60 ring-inset
 * - Ticket pin holes MUST have: shadow-[inset_0_1px_2px_rgba(0,0,0,0.1)]
 * - Children are wrapped in min-w-0 to avoid text blowing out width
 */
export default function ExpressiveCard({
  variant = 'glass',
  theme = 'blue',
  className,
  children,
  ...props
}) {
  const t = typeof theme === 'string' ? (THEMES[theme] || THEMES.blue) : theme;

  const isFlat = variant === 'flat';
  const isTicket = variant === 'ticket';

  const base = isFlat
    ? cx(
        'relative overflow-hidden',
        'rounded-2xl border border-gray-100 dark:border-gray-700',
        'shadow-sm',
        className
      )
    : cx(
        'relative overflow-hidden',
        // Double stroke (critical)
        'border border-white/60 ring-1 ring-white/60 ring-inset',
        // Outer hairline to keep edges visible on light backgrounds
        'outline outline-1 outline-black/5',
        // Glass body
        'bg-white/55 backdrop-blur-md',
        // Shape + elevation
        'rounded-2xl shadow-md-elevation-1',
        className
      );

  return (
    <div className={base} {...props}>
      {isTicket ? (
        <>
          {/* Left rail */}
          <div className={cx('absolute left-0 top-0 bottom-0 w-2', t?.rail)} />

          {/* Pin holes (cut-out effect) */}
          <div
            className={cx(
              'absolute left-2 top-9 -translate-x-1/2 w-3.5 h-3.5 rounded-full',
              'bg-background-warm',
              'border border-black/5',
              'shadow-[inset_0_1px_2px_rgba(0,0,0,0.1)]'
            )}
          />
          <div
            className={cx(
              'absolute left-2 bottom-9 -translate-x-1/2 w-3.5 h-3.5 rounded-full',
              'bg-background-warm',
              'border border-black/5',
              'shadow-[inset_0_1px_2px_rgba(0,0,0,0.1)]'
            )}
          />
        </>
      ) : null}

      <div className="min-w-0">{children}</div>
    </div>
  );
}


